import type { CuratedPlan } from '@/lib/fitnessPlans';
import type { Json } from '@/integrations/supabase/types';

export interface FitnessOnboardingDraft {
  goal: string; level: string; days: number; equipment: string[];
  plan: CuratedPlan; planName: string; planDescription: string;
}
export interface FitnessOnboardingProfile {
  user_id: string; goal: string; level: string; days_per_week: number; equipment: string[];
}
export interface FitnessOnboardingGateway {
  activate(planId: string, plan: Json, signal: AbortSignal): PromiseLike<{ data: unknown; error: unknown }>;
  saveProfile(profile: FitnessOnboardingProfile, signal: AbortSignal): PromiseLike<{ data: unknown; error: unknown }>;
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

/** Stable within one authenticated setup session, including a lost reply and reload.
 * Only an explicit setup action invokes this. These IDs grant no write authority. */
export async function fitnessOnboardingPlanId(ownerId: string, authSessionId: string, draft: FitnessOnboardingDraft): Promise<string> {
  if (!uuid.test(ownerId) || !uuid.test(authSessionId)) throw new Error('Fitness setup session unavailable');
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([
    'vyv-fitness-onboarding-v1', ownerId, authSessionId, draft.plan.id,
    draft.goal, draft.level, draft.days, [...draft.equipment].sort(),
    draft.plan.goal, draft.plan.level, draft.plan.days, draft.planName, draft.planDescription,
  ])))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 80; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
}

function validateDraft(draft: FitnessOnboardingDraft) {
  if (!['lose', 'muscle', 'endurance', 'general'].includes(draft.goal)
    || !['beginner', 'intermediate', 'advanced'].includes(draft.level)
    || !Number.isInteger(draft.days) || draft.days < 2 || draft.days > 6
    || !Array.isArray(draft.equipment) || draft.equipment.some(value => !['bodyweight', 'dumbbells', 'barbell', 'cables', 'full_gym', 'home_gym'].includes(value))
    || !draft.plan.id || !draft.plan.days.length || !draft.planName.trim()) throw new Error('Invalid fitness setup');
}

/** Existing transactional plan activation precedes profile completion.
 * A partial failure can leave the confirmed plan active, but cannot mark an
 * unfinished profile complete. Retrying uses the same plan ID, not another insert.
 * This is two confirmed operations, not a new cross-table transaction. */
export async function saveFitnessOnboarding(
  ownerId: string, planId: string, input: FitnessOnboardingDraft,
  gateway: FitnessOnboardingGateway, assertCurrent: () => void,
  options: { timeoutMs?: number } = {},
): Promise<void> {
  assertCurrent();
  const draft = structuredClone(input);
  validateDraft(draft);
  if (!uuid.test(ownerId) || !uuid.test(planId)) throw new Error('Invalid fitness setup owner');
  const controller = new AbortController();
  let expired = false;
  const check = () => { assertCurrent(); if (expired) throw new Error('Fitness setup reply expired'); };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => { expired = true; controller.abort(); reject(new Error('Fitness setup outcome unconfirmed')); }, options.timeoutMs ?? 20_000);
  });
  const work = async () => {
    const plan: Json = {
      name: draft.planName, description: draft.planDescription,
      days: draft.plan.days.map(day => ({
        name: day.name, exercises: day.exercises.map(exercise => ({ ...exercise })),
      })),
      goal: draft.plan.goal, level: draft.plan.level,
    };
    check();
    const activation = await gateway.activate(planId, plan, controller.signal);
    check();
    if (activation.error || activation.data !== planId) throw new Error('Fitness plan activation unconfirmed');
    const profile: FitnessOnboardingProfile = {
      user_id: ownerId, goal: draft.goal, level: draft.level,
      days_per_week: draft.days, equipment: [...new Set(draft.equipment)].sort(),
    };
    const result = await gateway.saveProfile(profile, controller.signal);
    check();
    if (result.error || !object(result.data) || result.data.user_id !== ownerId
      || result.data.goal !== profile.goal || result.data.level !== profile.level
      || result.data.days_per_week !== profile.days_per_week || !Array.isArray(result.data.equipment)
      || JSON.stringify([...result.data.equipment].sort()) !== JSON.stringify(profile.equipment)) {
      throw new Error('Fitness profile save unconfirmed');
    }
  };
  try { await Promise.race([work(), deadline]); }
  finally { if (timer !== undefined) clearTimeout(timer); controller.abort(); }
}
